import React, { useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  ChevronDown,
  Eraser,
  FileDown,
  Loader2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Bộ lọc trang Thời khóa biểu (/elearning/calendar).
 * Chi nhánh: khóa theo tài khoản đăng nhập; chỉ superadmin (canFilterAllBranches) được chọn.
 */
const ScheduleFilter = ({
  filters,
  setFilters,
  filterOptions = { branches: [], subjects: [], courses: [] },
  canFilterAllBranches = false,
  onRefresh,
  onExportPDF,
  isExporting,
  viewMode,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const isFiltering =
    (canFilterAllBranches && filters.branch_id !== "all") ||
    filters.subject_id !== "all" ||
    filters.course_id !== "all" ||
    (filters.venue && filters.venue !== "center");

  const handleFilterChange = (field, value) => {
    if (field === "branch_id" && !canFilterAllBranches) return;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters((prev) => ({
      branch_id: canFilterAllBranches ? "all" : prev.branch_id,
      subject_id: "all",
      course_id: "all",
      search: "",
      venue: "center",
    }));
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${
          active
            ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
            : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({
    label,
    value,
    options,
    onChange,
    placeholder,
    showAllOption = true,
    disabled = false,
  }) => (
    <div className="flex flex-col gap-1.5 w-full text-left">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm
                     disabled:bg-gray-50 disabled:opacity-80 disabled:cursor-not-allowed"
        >
          {showAllOption ? <option value="all">{placeholder}</option> : null}
          {(options || []).map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const venue = filters.venue || "center";
  const venueOptions = [
    { id: "center", label: "Trung tâm" },
    { id: "school", label: "Tại trường" },
    { id: "all", label: "Tất cả" },
  ];

  const branchOptions = filterOptions.branches || [];
  const branchSelectValue =
    !canFilterAllBranches &&
    branchOptions.length > 0 &&
    (filters.branch_id === "all" ||
      !branchOptions.some((o) => String(o.id) === String(filters.branch_id)))
      ? branchOptions[0].id
      : filters.branch_id;

  return (
    <div
      className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6"
      data-tour="schedule-filter-bar"
    >
      <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-gray-100">
        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest shrink-0">
          Địa điểm lớp
        </span>
        <div className="flex flex-wrap gap-2">
          {venueOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleFilterChange("venue", opt.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
                venue === opt.id
                  ? "bg-red-700 text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm theo lớp, giáo viên, phòng..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={() => (onRefresh ? onRefresh() : setFilters({ ...filters }))}
            title="Làm mới dữ liệu"
          />

          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />

          {isFiltering && (
            <button
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        <div className="hidden md:block">
          {viewMode === "month" && (
            <button
              disabled={isExporting}
              onClick={onExportPDF}
              className="flex items-center gap-2 px-6 py-2.5 bg-gray-800 text-white rounded-full font-bold hover:bg-black shadow-lg shadow-gray-200 transition-all active:scale-95 uppercase text-[11px] tracking-wider disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <FileDown size={18} />
              )}
              {isExporting ? "Đang xử lý..." : "Xuất PDF"}
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Cơ sở / Chi nhánh"
                value={branchSelectValue}
                options={branchOptions}
                onChange={(v) => handleFilterChange("branch_id", v)}
                placeholder="Tất cả chi nhánh"
                showAllOption={canFilterAllBranches}
                disabled={!canFilterAllBranches}
              />
              <SelectBox
                label="Môn học chuyên đề"
                value={filters.subject_id}
                options={filterOptions.subjects || []}
                onChange={(v) => handleFilterChange("subject_id", v)}
                placeholder="Tất cả môn học"
              />
              <SelectBox
                label="Chương trình đào tạo"
                value={filters.course_id}
                options={filterOptions.courses || []}
                onChange={(v) => handleFilterChange("course_id", v)}
                placeholder="Tất cả khóa học"
              />
            </div>
            {!canFilterAllBranches ? (
              <p className="mt-3 text-[11px] font-semibold text-gray-400">
                Chi nhánh đang khóa theo tài khoản đăng nhập. Chỉ Super Admin được chọn chi nhánh khác.
              </p>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ScheduleFilter;
